import { Head, Link } from '@inertiajs/react'
import { ArrowLeft, Pencil } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { companyPath, editFounderPath, foundersPath } from '@/lib/routes'
import { getRememberedUrl } from '@/lib/rememberedUrl'

type Country = { code: string; name: string; flag: string }

type Affiliation = {
  id: number
  company: {
    id: number
    name: string
    slug: string | null
    logo_url: string | null
  }
  role: string | null
  joined_at: string | null
  left_at: string | null
  active: boolean
}

type FounderDetails = {
  id: number
  slug: string
  name: string
  email: string | null
  gender: 'male' | 'female' | null
  linkedin_url: string | null
  twitter_url: string | null
  github_url: string | null
  website_url: string | null
  date_of_birth: string | null
  kalio_id: number | null
  avatar_url: string | null
  country: Country | null
  affiliations: Affiliation[]
}

type FoundersShowProps = { founder: FounderDetails }

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  )
}

function FoundersShow({ founder }: FoundersShowProps) {
  const backToIndex = getRememberedUrl('founders', foundersPath())
  const hasLinks =
    founder.linkedin_url || founder.twitter_url || founder.github_url || founder.website_url

  return (
    <>
      <Head title={`${founder.name} — Founders`} />

      <div className="mx-auto max-w-4xl px-8 py-10">
        <Link
          href={backToIndex}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to founders
        </Link>

        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
            {founder.avatar_url ? (
              <img
                src={founder.avatar_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-lg text-muted-foreground">
                {founder.name.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1">
            <PageHeader
              title={founder.name}
              description={
                founder.country ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span>{founder.country.flag}</span>
                    <span>{founder.country.name}</span>
                  </span>
                ) : null
              }
              actions={
                <Link href={editFounderPath(founder.slug)}>
                  <Button variant="outline">
                    <Pencil />
                    Edit
                  </Button>
                </Link>
              }
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Basic info</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Email">{founder.email ?? '—'}</Field>
            <Field label="Gender">{founder.gender ?? '—'}</Field>
            <Field label="Date of birth">{founder.date_of_birth ?? '—'}</Field>
            <Field label="Kalio ID">{founder.kalio_id ?? '—'}</Field>
          </CardContent>
        </Card>

        {hasLinks && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Links</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {founder.linkedin_url && (
                <Field label="LinkedIn">
                  <a
                    href={founder.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline-offset-4 hover:underline"
                  >
                    {founder.linkedin_url}
                  </a>
                </Field>
              )}
              {founder.twitter_url && (
                <Field label="X / Twitter">
                  <a
                    href={founder.twitter_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline-offset-4 hover:underline"
                  >
                    {founder.twitter_url}
                  </a>
                </Field>
              )}
              {founder.github_url && (
                <Field label="GitHub">
                  <a
                    href={founder.github_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline-offset-4 hover:underline"
                  >
                    {founder.github_url}
                  </a>
                </Field>
              )}
              {founder.website_url && (
                <Field label="Website">
                  <a
                    href={founder.website_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline-offset-4 hover:underline"
                  >
                    {founder.website_url}
                  </a>
                </Field>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Companies</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {founder.affiliations.length === 0 ? (
              <p className="px-6 py-8 text-sm text-muted-foreground">
                No company affiliations yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="px-6 py-2 text-left font-medium">Company</th>
                    <th className="px-6 py-2 text-left font-medium">Role</th>
                    <th className="px-6 py-2 text-left font-medium">From</th>
                    <th className="px-6 py-2 text-left font-medium">To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {founder.affiliations.map((a) => (
                    <tr key={a.id}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-muted">
                            {a.company.logo_url ? (
                              <img
                                src={a.company.logo_url}
                                alt=""
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <span className="text-[10px] text-muted-foreground">
                                {a.company.name.slice(0, 1).toUpperCase()}
                              </span>
                            )}
                          </div>
                          {a.company.slug ? (
                            <Link
                              href={companyPath(a.company.slug)}
                              className="underline-offset-4 hover:underline"
                            >
                              {a.company.name}
                            </Link>
                          ) : (
                            a.company.name
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">{a.role ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">{a.joined_at ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {a.active ? (
                          <span className="text-emerald-600 dark:text-emerald-400">active</span>
                        ) : (
                          (a.left_at ?? '—')
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(FoundersShow, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
